import { useState, useRef, useCallback } from "react"
import { message, Upload } from "antd"
import { isValidXlsxFile, parseXlsxFile, validateFileContent, createFileObject } from "../utils/fileValidation"
import { toaster } from "components/common/Toast"

/**
 * Custom hook for managing file upload state and logic
 * @param {Function} handleFileChangeImport - Callback when file is successfully validated
 * @param {Function} onFileListChange - Optional callback for file list changes
 * @returns {Object} - Upload state and handlers
 */
export const useFileUpload = (handleFileChangeImport, onFileListChange) => {
  const [fileList, setFileList] = useState([])
  const [uploading, setUploading] = useState(false)
  const [progress, setProgress] = useState(0)
  const [uploadComplete, setUploadComplete] = useState(false)
  const [error, setError] = useState("")
  const progressIntervalRef = useRef(null)

  /**
   * Clears the progress interval
   */
  const clearProgressInterval = useCallback(() => {
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current)
    }
  }, [])

  /**
   * Starts the progress animation
   */
  const startProgressAnimation = useCallback(() => {
    clearProgressInterval()
    setProgress(0)
    let currentProgress = 0

    progressIntervalRef.current = setInterval(() => {
      currentProgress += 10
      if (currentProgress <= 90) {
        setProgress(currentProgress)
      }
    }, 150)
  }, [clearProgressInterval])

  /**
   * Resets the upload state
   */
  const resetUploadState = useCallback(() => {
    setUploading(false)
    setProgress(0)
    setUploadComplete(false)
    setError("")
  }, [])

  /**
   * Handles file validation before upload
   */
  const beforeUpload = useCallback(
    async (file) => {
      // Validate file type
      if (!isValidXlsxFile(file)) {
        setError("Only .xlsx format is supported!")
        toaster.error("Only .xlsx format is supported!")
        return Upload.LIST_IGNORE
      }

      clearProgressInterval()

      // Add file to list and start upload
      const fileObj = createFileObject(file)
      setFileList([fileObj])
      setUploading(true)
      setUploadComplete(false)
      setError("")
      startProgressAnimation()

      try {
        // Parse and validate file content
        const rows = await parseXlsxFile(file)
        const validation = validateFileContent(rows)

        if (!validation.isValid) {
          clearProgressInterval()
          setError(validation.error)
          toaster.error(validation.error)
          resetUploadState()
          return Upload.LIST_IGNORE
        }

        // File is valid, proceed with import
        handleFileChangeImport(file)
        return true
      } catch (err) {
        clearProgressInterval()
        setError("Failed to read file.")
        toaster.error("Failed to read file. Please try again or check the file format.")
        resetUploadState()
        return Upload.LIST_IGNORE
      }
    },
    [clearProgressInterval, startProgressAnimation, resetUploadState, handleFileChangeImport]
  )

  /**
   * Handles file upload status changes
   */
  const handleChange = useCallback(
    (info) => {
      const { status } = info.file

      if (status === "done") {
        setUploading(false)
        setProgress(100)
        setUploadComplete(true)
      } else if (status === "error") {
        setError(`${info.file.name} upload failed.`)
        resetUploadState()
      }

      setFileList(info.fileList)
      onFileListChange?.(info.fileList)
    },
    [resetUploadState, onFileListChange]
  )

  /**
   * Handles file removal
   */
  const handleRemove = useCallback(
    (file) => {
      clearProgressInterval()

      const updatedList = fileList.filter((f) => f.uid !== file.uid)
      setFileList(updatedList)
      resetUploadState()
      handleFileChangeImport(null)
      return true
    },
    [fileList, clearProgressInterval, resetUploadState, handleFileChangeImport]
  )

  /**
   * Custom request handler for Upload component
   */
  const customRequest = useCallback(
    ({ onSuccess }) => {
      clearProgressInterval()

      // Complete the progress to 100%
      setProgress(95)
      setTimeout(() => {
        setProgress(100)
        setUploading(false)
        setUploadComplete(true)
        onSuccess("ok")
      }, 200)
    },
    [clearProgressInterval]
  )

  return {
    fileList,
    uploading,
    progress,
    uploadComplete,
    error,
    setError,
    beforeUpload,
    handleChange,
    handleRemove,
    customRequest
  }
}
